import { useContext, useMemo } from 'react';
import { DateTime } from 'luxon';
import { useQuery } from '@tanstack/react-query';
import { graphqlSdk } from '../Utils/graphql-client';
import useShopForPageType from './use-shop-for-page-type';
import {
  bookingState,
  BookingStateCombinedState,
} from '../Pages/Booking/client-state/booking-state-context';
import {
  bookingEditState,
  BookingEditStateCombinedState,
} from '../Pages/BookingEdit/client-state/booking-edit-state-context';
import usePageType from './use-page-type';

const getFromDate = (selectedDate: Date, timezone: string) => {
  // selectedDate is midnight in local systems timezone,
  // convert to shops timezone but don't change time
  const selectedDateMillis = DateTime.fromJSDate(selectedDate)
    .setZone(timezone, { keepLocalTime: true })
    .startOf('day')
    .toMillis();

  const nowMillis = DateTime.now().setZone(timezone).startOf('day').toMillis();

  const isToday = selectedDateMillis === nowMillis;
  // If selectedDate is today, return now in shops timezone
  if (isToday) return DateTime.now().setZone(timezone).toISO();

  // If selectedDate is NOT today, return start of day in shops timezone
  return DateTime.fromJSDate(selectedDate)
    .setZone(timezone, { keepLocalTime: true })
    .startOf('day')
    .toISO();
};

const useTimeslots = () => {
  const shop = useShopForPageType();
  const pageType = usePageType();

  // This hook is currently only used on the booking and edit-booking pages
  const { state: stateBooking } = useContext(bookingState);
  const { state: stateBookingEdit } = useContext(bookingEditState);

  let state: BookingStateCombinedState['state'] | BookingEditStateCombinedState['state'];

  switch (pageType) {
    case 'booking': {
      state = stateBooking;
      break;
    }
    case 'edit-booking': {
      state = stateBookingEdit;
      break;
    }
    default: {
      state = stateBooking;
      break;
    }
  }

  const variables = useMemo(() => {
    if (!state.selectedDate || !shop?.id) return undefined;

    const fromDate = getFromDate(state.selectedDate, shop.timezoneName);
    const toDate = DateTime.fromJSDate(state.selectedDate)
      .setZone(shop.timezoneName, { keepLocalTime: true })
      .endOf('day')
      .toISO();

    if (!fromDate || !toDate) return undefined;

    const variables = {
      shopId: shop.id,
      fromDate,
      toDate,
      excludeBookingId: state.booking.id,
      pickupLocationId: state.booking.pickupLocationId,
      dropoffLocationId: state.booking.dropoffLocationId,
    };

    return variables;
  }, [
    state.booking.id,
    state.booking.dropoffLocationId,
    state.booking.pickupLocationId,
    state.selectedDate,
    shop?.id,
    shop?.timezoneName,
  ]);

  return useQuery({
    queryKey: ['timeslots', variables],
    queryFn: async () => {
      if (!variables) return [];

      const data = await graphqlSdk.getTimeslots(variables);
      console.log('useTimeslotsQuery timeslots', data.getTimeslots);
      return data.getTimeslots;
    },
    enabled: Boolean(shop?.id && state.selectedDate && variables),
    placeholderData: [],
    keepPreviousData: true,
    // Timeslots are considered fresh for 1 minute
    // A query with the same variables will at most be made every minute
    staleTime: 1000 * 60 * 1,
    // Set cache time to 5 minutes, which is the default
    cacheTime: 1000 * 60 * 5,
  });
};

export default useTimeslots;
